import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Empty, Pill } from "../components/Bits";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StartBatchDialog } from "../components/StartBatchDialog";
import { toast } from "sonner";
import { PlusIcon, BroadcastIcon, PauseIcon, ClockIcon } from "@phosphor-icons/react";
import { formatRelative } from "../lib/format";
import { TIMEZONE_GROUPS } from "../lib/timezones";

const STATUS_COLORS = {
    draft: "#4B5563",
    active: "#E02424",
    paused: "#D97706",
    completed: "#059669",
    scheduled: "#0EA5E9",
};

export default function Campaigns() {
    const [campaigns, setCampaigns] = useState([]);
    const [scripts, setScripts] = useState([]);
    const [lists, setLists] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [startDialog, setStartDialog] = useState(null); // campaign object or null
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "", script_id: "", list_id: "",
        daily_limit: 100, max_attempts: 3,
        success_criteria: "appointment_booked",
        voice: "professional", pace: "normal", tone: "neutral",
        timezone: "America/New_York", start_hour: 9, end_hour: 17, weekdays_only: true,
    });

    const load = async () => {
        const [c, s, l] = await Promise.all([
            api.get("/campaigns"),
            api.get("/scripts"),
            api.get("/prospect-lists"),
        ]);
        setCampaigns(c.data); setScripts(s.data); setLists(l.data);
        if (!form.script_id && s.data[0]) setForm((f) => ({ ...f, script_id: s.data[0].id }));
        if (!form.list_id && l.data[0]) setForm((f) => ({ ...f, list_id: l.data[0].id }));
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const create = async () => {
        if (!form.name.trim() || !form.script_id || !form.list_id) {
            toast.error("Name, script, and list are required");
            return;
        }
        const payload = {
            name: form.name,
            script_id: form.script_id,
            list_id: form.list_id,
            daily_limit: Number(form.daily_limit),
            max_attempts: Number(form.max_attempts),
            success_criteria: form.success_criteria,
            voice_config: { voice: form.voice, pace: form.pace, tone: form.tone },
            business_hours: {
                timezone: form.timezone,
                start_hour: Number(form.start_hour),
                end_hour: Number(form.end_hour),
                weekdays_only: form.weekdays_only,
            },
        };
        try {
            const { data } = await api.post("/campaigns", payload);
            setShowNew(false);
            await load();
            toast.success("Campaign created");
            navigate(`/campaigns/${data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const startBatch = async (id, override = false) => {
        setBusyId(id);
        try {
            const { data } = await api.post(`/campaigns/${id}/start`, { batch_size: 5, override_business_hours: override });
            const mode = data.mode === "vapi" ? "real (Vapi)" : "mock";
            toast.success(`Placed ${data.calls_completed} ${mode} call${data.calls_completed === 1 ? "" : "s"}`);
            if (data.errors && data.errors.length) {
                toast.error(`${data.errors.length} call(s) failed — see audit log`);
            }
            setStartDialog(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Start failed";
            if (e?.response?.status === 400 && typeof detail === "string" && detail.includes("Outside business hours")) {
                if (window.confirm(`${detail}\n\nOverride and place calls anyway?`)) {
                    setBusyId(null);
                    return startBatch(id, true);
                }
                toast.error(detail);
            } else {
                toast.error(detail);
            }
        } finally { setBusyId(null); }
    };

    const scheduleBatch = async (id) => {
        setBusyId(id);
        try {
            const { data } = await api.post(`/campaigns/${id}/schedule`, { batch_size: 5 });
            const when = new Date(data.scheduled_for).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
            toast.success(`Scheduled to run ${when}`);
            setStartDialog(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Schedule failed");
        } finally { setBusyId(null); }
    };

    const pause = async (id) => {
        await api.post(`/campaigns/${id}/pause`);
        toast.success("Paused");
        await load();
    };

    return (
        <div className="pb-12">
            <PageHeader
                overline="OPERATIONS"
                title="Campaigns"
                description="Create, schedule, and run AI calling campaigns against your prospect lists."
                testid="campaigns-header"
                actions={
                    <button type="button" onClick={() => setShowNew(true)} className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="new-campaign-button">
                        <PlusIcon size={14} weight="bold" /> New campaign
                    </button>
                }
            />

            <div className="px-8 py-6">
                {campaigns.length === 0 ? (
                    <Empty
                        title="No campaigns yet"
                        description="A campaign pairs a prospect list with a script and the agent voice config."
                        action={<button type="button" onClick={() => setShowNew(true)} className="btn-primary px-4 py-2 text-[13px]" data-testid="empty-create-campaign">Create campaign</button>}
                        testid="campaigns-empty"
                    />
                ) : (
                    <div className="swiss-section overflow-x-auto">
                        <table className="w-full text-[13px] min-w-[960px]" data-testid="campaigns-table">
                            <thead>
                                <tr className="border-b border-[var(--border)] [&>th]:py-3 [&>th]:px-4 [&>th]:overline [&>th]:text-[10px] text-left">
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Script</th>
                                    <th>List</th>
                                    <th>Calls</th>
                                    <th>Booked</th>
                                    <th>Updated</th>
                                    <th className="text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map((c) => {
                                    const script = scripts.find((s) => s.id === c.script_id);
                                    const list = lists.find((l) => l.id === c.list_id);
                                    return (
                                        <tr key={c.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-active)]" data-testid={`campaign-row-${c.id}`}>
                                            <td className="py-3 px-4 font-semibold max-w-[220px]">
                                                <Link to={`/campaigns/${c.id}`} className="hover:underline block truncate">{c.name}</Link>
                                            </td>
                                            <td className="py-3 px-4 whitespace-nowrap">
                                                <Pill color={STATUS_COLORS[c.status]}>
                                                    {c.status === "active" && <span className="w-1.5 h-1.5 rounded-full live-dot mr-1 inline-block" />}
                                                    {c.status}
                                                </Pill>
                                            </td>
                                            <td className="py-3 px-4 truncate max-w-[200px]">{script?.name || "—"}</td>
                                            <td className="py-3 px-4 truncate max-w-[180px]">{list?.name || "—"}</td>
                                            <td className="py-3 px-4 mono">{c.total_calls}</td>
                                            <td className="py-3 px-4 mono text-[var(--success)]">{c.appointments_booked}</td>
                                            <td className="py-3 px-4 mono text-[var(--text-secondary)] whitespace-nowrap">{formatRelative(c.updated_at)}</td>
                                            <td className="py-3 px-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => setStartDialog(c)}
                                                        disabled={busyId === c.id}
                                                        className="btn-primary px-3 py-1.5 text-[12px] inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                                                        data-testid={`start-batch-${c.id}`}
                                                    >
                                                        <BroadcastIcon size={12} weight="bold" /> {busyId === c.id ? "Working…" : "Start batch"}
                                                    </button>
                                                    {(c.status === "active" || c.status === "scheduled") && (
                                                        <button type="button" onClick={() => pause(c.id)} className="btn-ghost px-3 py-1.5 text-[12px] inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`pause-${c.id}`}>
                                                            <PauseIcon size={12} weight="bold" /> {c.status === "scheduled" ? "Cancel" : "Pause"}
                                                        </button>
                                                    )}
                                                </div>
                                                {c.status === "scheduled" && c.scheduled_for && (
                                                    <div className="mt-1 text-[10px] mono text-[#0EA5E9] flex items-center gap-1 justify-end" data-testid={`scheduled-for-${c.id}`}>
                                                        <ClockIcon size={10} weight="bold" />
                                                        {new Date(c.scheduled_for).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="rounded-[2px] sm:max-w-[640px]" data-testid="new-campaign-dialog">
                    <DialogHeader>
                        <DialogTitle>Create campaign</DialogTitle>
                        <DialogDescription>Pair a script with a prospect list, then configure your AI agent&apos;s voice and schedule.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Name" full>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="auth-input-c" data-testid="campaign-name" placeholder="Q2 Mid-Market — Whitepaper Push" />
                        </Field>
                        <Field label="Script">
                            <select value={form.script_id} onChange={(e) => setForm({ ...form, script_id: e.target.value })} className="auth-input-c" data-testid="campaign-script">
                                {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Prospect list">
                            <select value={form.list_id} onChange={(e) => setForm({ ...form, list_id: e.target.value })} className="auth-input-c" data-testid="campaign-list">
                                {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.record_count})</option>)}
                            </select>
                        </Field>
                        <Field label="Daily limit">
                            <input type="number" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: e.target.value })} className="auth-input-c" data-testid="campaign-daily-limit" min={1} />
                        </Field>
                        <Field label="Max attempts">
                            <input type="number" value={form.max_attempts} onChange={(e) => setForm({ ...form, max_attempts: e.target.value })} className="auth-input-c" data-testid="campaign-max-attempts" min={1} max={10} />
                        </Field>
                        <Field label="Success criteria">
                            <select value={form.success_criteria} onChange={(e) => setForm({ ...form, success_criteria: e.target.value })} className="auth-input-c" data-testid="campaign-success">
                                <option value="appointment_booked">Appointment booked</option>
                                <option value="whitepaper_sent">Whitepaper sent</option>
                                <option value="callback_requested">Callback requested</option>
                            </select>
                        </Field>
                        <Field label="Voice">
                            <select value={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.value })} className="auth-input-c" data-testid="campaign-voice">
                                <option value="professional">Simon · natural, warm (Vapi Nico V2)</option>
                                <option value="friendly">Alex · Canadian, conversational (Vapi Elliot)</option>
                                <option value="authoritative">Godfrey · deeper, authoritative (Vapi V2)</option>
                            </select>
                        </Field>
                        <Field label="Pace">
                            <select value={form.pace} onChange={(e) => setForm({ ...form, pace: e.target.value })} className="auth-input-c" data-testid="campaign-pace">
                                <option value="slow">Slow</option><option value="normal">Normal</option><option value="fast">Fast</option>
                            </select>
                        </Field>
                        <Field label="Tone">
                            <select value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} className="auth-input-c" data-testid="campaign-tone">
                                <option value="formal">Formal</option><option value="neutral">Neutral</option><option value="warm">Warm</option>
                            </select>
                        </Field>
                        <Field label="Timezone">
                            <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="auth-input-c" data-testid="campaign-tz">
                                {TIMEZONE_GROUPS.map((g) => (
                                    <optgroup key={g.region} label={g.region}>
                                        {g.zones.map((z) => (
                                            <option key={z.value} value={z.value}>{z.label}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <TzHint tz={form.timezone} startHour={form.start_hour} endHour={form.end_hour} weekdaysOnly={form.weekdays_only} />
                        </Field>
                        <Field label="Start hour">
                            <input type="number" value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: e.target.value })} className="auth-input-c" data-testid="campaign-start" min={0} max={23} />
                        </Field>
                        <Field label="End hour">
                            <input type="number" value={form.end_hour} onChange={(e) => setForm({ ...form, end_hour: e.target.value })} className="auth-input-c" data-testid="campaign-end" min={0} max={23} />
                        </Field>
                        <Field label="Weekdays only">
                            <select value={form.weekdays_only ? "1" : "0"} onChange={(e) => setForm({ ...form, weekdays_only: e.target.value === "1" })} className="auth-input-c" data-testid="campaign-weekdays">
                                <option value="1">Mon–Fri only</option>
                                <option value="0">All days</option>
                            </select>
                        </Field>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => setShowNew(false)} className="btn-ghost px-4 py-2 text-[13px]" data-testid="campaign-cancel">Cancel</button>
                        <button type="button" onClick={create} className="btn-primary px-4 py-2 text-[13px]" data-testid="campaign-submit">Create</button>
                    </DialogFooter>
                    <style>{`.auth-input-c{width:100%;padding:9px 10px;border:1px solid var(--border);border-radius:2px;font-size:13px;background:#fff;font-family:'IBM Plex Sans';}.auth-input-c:focus{outline:none;border-color:var(--accent);}`}</style>
                </DialogContent>
            </Dialog>

            <StartBatchDialog
                campaign={startDialog}
                onClose={() => setStartDialog(null)}
                onStartNow={() => startDialog && startBatch(startDialog.id)}
                onSchedule={() => startDialog && scheduleBatch(startDialog.id)}
                busy={busyId === startDialog?.id}
            />
        </div>
    );
}

const Field = ({ label, children, full }) => (
    <label className={`block ${full ? "col-span-2" : ""}`}>
        <span className="overline mb-1.5 block">{label}</span>
        {children}
    </label>
);


function TzHint({ tz, startHour, endHour, weekdaysOnly }) {
    const [now, setNow] = React.useState(new Date());
    React.useEffect(() => {
        const i = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(i);
    }, []);
    let localStr = "—";
    let isOpen = false;
    try {
        const fmt = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit",
            weekday: "short",
            hour12: false,
        });
        localStr = fmt.format(now);
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", hour12: false, weekday: "short" }).formatToParts(now);
        const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
        const wk = parts.find((p) => p.type === "weekday")?.value || "Mon";
        const weekend = wk === "Sat" || wk === "Sun";
        isOpen = (!weekdaysOnly || !weekend) && h >= Number(startHour) && h < Number(endHour);
    } catch (e) {
        /* invalid tz */
    }
    const color = isOpen ? "var(--success)" : "var(--warning)";
    const label = isOpen ? "WITHIN BUSINESS HOURS" : "OUTSIDE BUSINESS HOURS";
    return (
        <div className="mt-1.5 flex items-center gap-2 text-[11px] font-mono" data-testid="campaign-tz-hint">
            <span className="text-[var(--text-secondary)]">Local:</span>
            <span className="font-semibold">{localStr}</span>
            <span style={{ color }} className="uppercase tracking-wider">· {label}</span>
        </div>
    );
}
